import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { ContextMenuComplete, ZAvatar, Logo } from '@marageti/z4-lib';
import {
  Conversation,
  ConversationMessageState,
  ConversationStateEntry,
  ConversationType,
} from '@marageti/z4-sdk/lib/travel';
import { format } from 'date-fns';

type ConversationAvatarProps = {
  conversationType: ConversationType;
  hasNew: boolean;
  travelerName: string;
  travelerAvatar?: string;
};

const getTitle = (conversationType: ConversationType, travelerName: string) => {
  if (conversationType === ConversationType.TravelerToPartner) {
    return travelerName;
  }
  return `Zicasso | ${travelerName}`;
};

const getDate = (conversation: Conversation, agentId: string) => {
  const date = conversation.messageState.find(
    (c: ConversationStateEntry) => c.personId !== '' && c.personId === agentId,
  )?.lastMessageReceivedAt;
  // if date is not valid (0001-01-01T00:00:00Z), return empty string
  if (!date || date === '0001-01-01T00:00:00Z') return '';
  return format(new Date(date), 'MMM d');
};

const getHasNew = (id: string, messageState: ConversationMessageState) => {
  const userMessageState = messageState.find((c: ConversationStateEntry) => c.personId !== '' && c.personId === id);
  return userMessageState?.hasNew || false;
};

const ConversationAvatar = ({
  conversationType,
  hasNew,
  travelerName,
  travelerAvatar,
}: ConversationAvatarProps) => {
  if (conversationType === ConversationType.TravelerToPartner) {
    return (
      <Badge invisible={!hasNew} color="info" variant="dot">
        <ZAvatar
          size={40}
          alt={travelerName}
          name={travelerName}
          src={travelerAvatar}
        />
      </Badge>
    );
  }
  return (
    <Badge invisible={!hasNew} color="info" variant="dot">
      <ZAvatar
        size={40}
        alt="Zicasso Care Team"
        name="Z"
        src={Logo}
      />
    </Badge>
  );
};

type ConversationListProps = {
  conversationsMap: Record<string, Conversation>;
  hasMoreConversations?: boolean;
  isValidating?: boolean;
  markConversationRead: (id: string) => void;
  markConversationUnread: (id: string) => void;
  onLoadMoreConversations?: () => void;
  onSelectConversation: (conversationId: string) => void;
  selectedInboxConversationId: string | undefined;
  selectedAgentId: string;
};

const ConversationList = ({
  conversationsMap,
  hasMoreConversations,
  isValidating,
  markConversationRead,
  markConversationUnread,
  onLoadMoreConversations,
  onSelectConversation,
  selectedInboxConversationId,
  selectedAgentId,
}: ConversationListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);
  const scrollRestoreRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const conversationIds = Object.keys(conversationsMap);
  const count = conversationIds.length;

  // When new items are added (load more), preserve scroll position so the list doesn’t jump
  useEffect(() => {
    if (count > prevCountRef.current && prevCountRef.current > 0 && scrollRef.current) {
      const prev = scrollRestoreRef.current;
      if (prev) {
        // New items are appended below; restore the same scrollTop so the view stays in place
        const rafId = requestAnimationFrame(() => {
          if (!scrollRef.current) return;
          scrollRef.current.scrollTop = prev.scrollTop;
        });
        scrollRestoreRef.current = null;
        setIsLoadingMore(false);
        prevCountRef.current = count;
        return () => cancelAnimationFrame(rafId);
      }
    }
    prevCountRef.current = count;
    return undefined;
  }, [count]);

  // Clear loading state when the load-more request finishes (e.g. on error or empty page)
  useEffect(() => {
    if (isLoadingMore && isValidating === false) {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, isValidating]);

  const handleLoadMore = () => {
    if (scrollRef.current) {
      scrollRestoreRef.current = {
        scrollHeight: scrollRef.current.scrollHeight,
        scrollTop: scrollRef.current.scrollTop,
      };
    }
    setIsLoadingMore(true);
    onLoadMoreConversations?.();
  };

  return (
    <Box
      ref={scrollRef}
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        overflowY: { desktop: 'auto' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <List
        component={Stack}
        spacing={0.5}
        sx={{
          width: '100%',
          flex: '0 0 auto',
        }}
      >
        {Object.values(conversationsMap).map((conversation: Conversation) => {
          const dateText = getDate(conversation, selectedAgentId);
          return (
            <ListItem
              key={conversation.id}
              sx={{ width: '100%' }}
              disablePadding
            >
              <Card
                sx={{
                  width: '100%',
                  minHeight: 76,
                  borderRadius: 2,
                  boxShadow: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'stretch',
                  backgroundColor: selectedInboxConversationId === conversation.id ? 'neutral.main' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  px: 1.5,
                  mr: 2,
                }}
              >
                <ListItemButton
                  onClick={() => onSelectConversation(conversation.id)}
                  disableGutters
                  disableRipple
                >
                  <ListItemIcon>
                    <ConversationAvatar
                      conversationType={conversation.conversationType}
                      travelerName={conversation.primaryTravelerName || 'Traveler'}
                      travelerAvatar={conversation.primaryTravelerAvatar ?? undefined}
                      hasNew={getHasNew(selectedAgentId, conversation.messageState)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={(
                      <Typography variant="bodyBold" noWrap>
                        {getTitle(conversation.conversationType, conversation.primaryTravelerName || 'Traveler')}
                      </Typography>
                      )}
                    secondary={dateText ? (
                      <Typography variant="caption" noWrap>
                        {dateText}
                      </Typography>
                    ) : null}
                    disableTypography
                  />
                </ListItemButton>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ContextMenuComplete
                    id={`${conversation.id}-menu`}
                    items={
                      getHasNew(selectedAgentId, conversation.messageState)
                        ? [
                          {
                            label: 'Mark as Read',
                            action: () => markConversationRead(conversation.id),
                          },
                        ]
                        : [
                          {
                            label: 'Mark as Unread',
                            action: () => markConversationUnread(conversation.id),
                          },
                        ]
                    }
                  />
                </Box>
              </Card>
            </ListItem>
          );
        })}
      </List>
      {hasMoreConversations && (
        <Box sx={{ flex: '0 0 auto', px: 2, py: 1.5 }}>
          <Button
            variant="outlined"
            fullWidth
            disabled={isLoadingMore}
            onClick={handleLoadMore}
            startIcon={isLoadingMore ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isLoadingMore ? 'Loading…' : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ConversationList;
